import { useCallback, useEffect, useState } from "react";
import { Save, X } from "lucide-react";

import {
  RiskService,
  type MethodologyConfigResponse,
  type MethodologyConfigUpdate,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

interface FormState {
  weight_security: string;
  weight_policy: string;
  weight_asset: string;
  weight_compliance: string;
  threshold_critical: string;
  threshold_needs_attention: string;
  threshold_good: string;
}

function initialForm(cfg: MethodologyConfigResponse | null): FormState {
  return {
    weight_security: cfg?.weight_security ?? "0.30",
    weight_policy: cfg?.weight_policy ?? "0.25",
    weight_asset: cfg?.weight_asset ?? "0.20",
    weight_compliance: cfg?.weight_compliance ?? "0.25",
    threshold_critical: String(cfg?.threshold_critical ?? 40),
    threshold_needs_attention: String(cfg?.threshold_needs_attention ?? 60),
    threshold_good: String(cfg?.threshold_good ?? 80),
  };
}

export function RiskMethodologyTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canEdit = user ? TENANT_ADMIN_ROLES.has(user.role) : false;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() => initialForm(null));

  const fetcher = useCallback(() => RiskService.getMethodology(), []);
  const { data, error, isLoading, refetch } =
    useApi<MethodologyConfigResponse>(fetcher);

  const update = useMutation<
    MethodologyConfigUpdate,
    MethodologyConfigResponse
  >((body) => RiskService.updateMethodology(body), {
    onSuccess: () => {
      setEditing(false);
      void refetch();
    },
  });

  // Hydrate the form from the server response whenever a fresh
  // config lands. Avoid clobbering in-flight edits — if the user is
  // already editing, leave their draft alone.
  useEffect(() => {
    if (!editing && data) setForm(initialForm(data));
  }, [data, editing]);

  const weights = [
    Number(form.weight_security),
    Number(form.weight_policy),
    Number(form.weight_asset),
    Number(form.weight_compliance),
  ];
  const weightSum = weights.reduce((acc, w) => acc + (Number.isFinite(w) ? w : 0), 0);
  const weightSumOk = Math.abs(weightSum - 1) < 0.001;

  const tCritical = Number(form.threshold_critical);
  const tWarn = Number(form.threshold_needs_attention);
  const tGood = Number(form.threshold_good);
  const thresholdsOrdered = tCritical < tWarn && tWarn < tGood;
  const thresholdsInRange = [tCritical, tWarn, tGood].every(
    (n) => Number.isFinite(n) && n >= 0 && n <= 100
  );

  const canSubmit = weightSumOk && thresholdsOrdered && thresholdsInRange;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await update.mutate({
      weight_security: Number(form.weight_security),
      weight_policy: Number(form.weight_policy),
      weight_asset: Number(form.weight_asset),
      weight_compliance: Number(form.weight_compliance),
      threshold_critical: tCritical,
      threshold_needs_attention: tWarn,
      threshold_good: tGood,
    });
  };

  const handleCancel = () => {
    setEditing(false);
    if (data) setForm(initialForm(data));
    update.reset();
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  return (
    <div className="space-y-6">
      {update.error && (
        <Alert variant="danger">{parseApiError(update.error)}</Alert>
      )}

      <Section
        title={t("risk.methodology.weightsTitle")}
        description={t("risk.methodology.weightsDescription")}
        action={
          canEdit && !editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              {t("risk.methodology.edit")}
            </Button>
          ) : undefined
        }
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-3 tablet:grid-cols-4">
            <FormField
              label={t("risk.methodology.weightSecurity")}
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={form.weight_security}
              onChange={(v) => setForm({ ...form, weight_security: v })}
              disabled={!editing}
            />
            <FormField
              label={t("risk.methodology.weightPolicy")}
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={form.weight_policy}
              onChange={(v) => setForm({ ...form, weight_policy: v })}
              disabled={!editing}
            />
            <FormField
              label={t("risk.methodology.weightAsset")}
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={form.weight_asset}
              onChange={(v) => setForm({ ...form, weight_asset: v })}
              disabled={!editing}
            />
            <FormField
              label={t("risk.methodology.weightCompliance")}
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={form.weight_compliance}
              onChange={(v) => setForm({ ...form, weight_compliance: v })}
              disabled={!editing}
            />
          </div>

          <div
            className={
              "rounded-md border px-3 py-2 text-xs " +
              (weightSumOk
                ? "border-success-600/30 bg-success-50 text-success-600"
                : "border-warning-600/30 bg-warning-50 text-warning-600")
            }
          >
            {t("risk.methodology.weightSumLabel", {
              sum: weightSum.toFixed(2),
            })}{" "}
            {weightSumOk
              ? t("risk.methodology.weightSumValid")
              : t("risk.methodology.weightSumInvalid")}
          </div>

          <div>
            <h3 className="text-sm font-medium text-ocean-900">
              {t("risk.methodology.thresholdsTitle")}
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              {t("risk.methodology.thresholdsDescription")}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 tablet:grid-cols-3">
              <FormField
                label={t("risk.methodology.thresholdCritical")}
                type="number"
                step="1"
                min={0}
                max={100}
                value={form.threshold_critical}
                onChange={(v) => setForm({ ...form, threshold_critical: v })}
                disabled={!editing}
              />
              <FormField
                label={t("risk.methodology.thresholdNeedsAttention")}
                type="number"
                step="1"
                min={0}
                max={100}
                value={form.threshold_needs_attention}
                onChange={(v) =>
                  setForm({ ...form, threshold_needs_attention: v })
                }
                disabled={!editing}
              />
              <FormField
                label={t("risk.methodology.thresholdGood")}
                type="number"
                step="1"
                min={0}
                max={100}
                value={form.threshold_good}
                onChange={(v) => setForm({ ...form, threshold_good: v })}
                disabled={!editing}
              />
            </div>
            {!thresholdsOrdered && (
              <p className="mt-2 text-xs text-warning-600">
                {t("risk.methodology.thresholdsOrderError")}
              </p>
            )}
            {!thresholdsInRange && (
              <p className="mt-2 text-xs text-warning-600">
                {t("risk.methodology.thresholdsRangeError")}
              </p>
            )}
          </div>

          {editing && (
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={update.isLoading}
              >
                <X size={14} />
                {t("risk.methodology.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={update.isLoading || !canSubmit}
              >
                <Save size={14} />
                {update.isLoading
                  ? t("risk.methodology.saveLoading")
                  : t("risk.methodology.save")}
              </Button>
            </div>
          )}

          {!canEdit && (
            <p className="text-xs text-neutral-500">
              {t("risk.methodology.readOnlyNotice")}
            </p>
          )}
        </form>
      </Section>
    </div>
  );
}
